import React from 'react'
import type { RubroRow } from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import { rubroHasValidSelectedSkill } from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import { buildDefaultTechnicalSpecState } from '../../../../../utils/conversationalMode/technicalSpecStateUtils.ts'
import {
  canEditTechnicalSpecState,
  rubroTechnicalSpecLegajoInPdf,
  technicalSpecHasUsableContent,
  type DocumentationReviewContext
} from '../../../../../utils/conversationalMode/projectAutofillWorkflow.ts'
import RubroDeliverableSlotActions from './RubroDeliverableSlotActions.tsx'
import { buildSlotOriginActionMeta } from './rubroDeliverableOriginUtils.ts'
import type { RubroSlotOriginHandlers } from './RubroSlotOriginHandlers.ts'
import type { RubroDeliverableSourceLineProps } from './rubroDeliverableSourceLineProps.ts'
import styles from './BudgetSheetCompactEntry.module.scss'
import ProjectAutofillInfoNote from './ProjectAutofillInfoNote.tsx'

type SourceLineProps = RubroDeliverableSourceLineProps

type Props = {
  tempId: string
  rubro: RubroRow
  draftLocked: boolean
  isSaving: boolean
  documentationReview: DocumentationReviewContext
  updateRubro: (tempId: string, updates: Partial<Omit<RubroRow, 'tempId'>>) => void
  onOpenFullscreen: (tempId: string) => void
  sourceLineProps: SourceLineProps
  originHandlers: RubroSlotOriginHandlers
  originPickerOpen: boolean
  onOriginPickerOpenChange: (open: boolean) => void
  removedSnapshot: Partial<Omit<RubroRow, 'tempId'>> | null
  onRemovedSnapshotChange: (snapshot: Partial<Omit<RubroRow, 'tempId'>> | null) => void
}

const TechnicalSpecCompactEntry: React.FC<Props> = ({
  tempId,
  rubro,
  draftLocked,
  isSaving,
  documentationReview,
  updateRubro,
  onOpenFullscreen,
  sourceLineProps,
  originHandlers,
  originPickerOpen,
  onOriginPickerOpenChange,
  removedSnapshot,
  onRemovedSnapshotChange
}) => {
  if (draftLocked) return null

  const legajoPdf = rubroTechnicalSpecLegajoInPdf(rubro)
  const ts = rubro.technicalSpecificationState
  const hasContent = technicalSpecHasUsableContent(ts)
  const canSkill = rubroHasValidSelectedSkill(rubro)
  const saving = isSaving
  const canEdit = canEditTechnicalSpecState(
    rubro.technicalSpecEligibility,
    documentationReview.reviewStage
  )
  const rubroRef = {
    tempId,
    detectedLabel: rubro.detectedLabel,
    selectedSkillId: rubro.selectedSkillId
  }
  const showRemove = legajoPdf || hasContent || !!ts
  const originAction = buildSlotOriginActionMeta({ slot: 'technicalSpecifications', ...sourceLineProps })
  const sectionCount = ts?.sections.filter((s) => String(s.body ?? '').trim()).length ?? 0
  const sheetMeta = legajoPdf
    ? originAction.meta
    : ts
      ? `${sectionCount} sección${sectionCount === 1 ? '' : 'es'} con texto`
      : null
  const sheetPdfHref = legajoPdf ? originAction.pdfHref : null

  const ensureState = () => {
    if (ts) return
    updateRubro(tempId, { technicalSpecificationState: buildDefaultTechnicalSpecState(rubroRef) })
  }

  const handleOpenFullscreen = () => {
    if (!canSkill || saving || legajoPdf) return
    ensureState()
    onOpenFullscreen(tempId)
  }

  const canOpenEditor = canSkill && !legajoPdf
  const primaryLabel = ts && hasContent ? 'Ver y editar' : 'Completar especificaciones manualmente'
  const slotActions = showRemove || originPickerOpen || canOpenEditor ? (
    <RubroDeliverableSlotActions
      slot="technicalSpecifications"
      showRemove={showRemove}
      showPrimary={canOpenEditor}
      primaryLabel={primaryLabel}
      primaryDisabled={saving || !canEdit}
      onPrimary={handleOpenFullscreen}
      meta={sheetMeta}
      pdfHref={sheetPdfHref}
      originPickerOpen={originPickerOpen}
      onOriginPickerOpenChange={onOriginPickerOpenChange}
      removedSnapshot={removedSnapshot}
      onRemovedSnapshotChange={onRemovedSnapshotChange}
      {...originHandlers}
    />
  ) : null

  if (documentationReview.isProcessing) {
    return (
      <div className={styles.blockEmbedded}>
        <ProjectAutofillInfoNote className={styles.hint}>
          Se generará cuando termine el análisis del documento.
        </ProjectAutofillInfoNote>
      </div>
    )
  }

  if (legajoPdf && !originPickerOpen) {
    return (
      <div className={styles.blockEmbedded}>
        <p className={styles.sheetOfferText}>
          <strong>Enviaremos</strong> las especificaciones técnicas desde las páginas del PDF que detectamos para este
          rubro.
        </p>
        {slotActions}
      </div>
    )
  }

  if (rubro.technicalSpecEligibility === 'generation_failed' && !hasContent && !originPickerOpen) {
    const reason = rubro.technicalSpecificationSuggestionReason?.trim()
    return (
      <div className={styles.blockEmbedded}>
        <p className={styles.sheetOfferText}>
          <strong>No encontramos</strong> especificaciones técnicas en el documento para este rubro.
          {reason ? ` ${reason}` : ''}
        </p>
        {!canSkill ? (
          <ProjectAutofillInfoNote className={styles.hint}>
            Elegí el rubro de la lista para continuar.
          </ProjectAutofillInfoNote>
        ) : null}
        {slotActions}
      </div>
    )
  }

  if (!hasContent && !ts && !originPickerOpen) {
    return (
      <div className={styles.blockEmbedded}>
        <p className={styles.sheetOfferText}>
          <strong>No encontramos</strong> especificaciones técnicas en el documento. Podés cargarlas manualmente.
        </p>
        {slotActions}
      </div>
    )
  }

  if (originPickerOpen) {
    return <div className={styles.blockEmbedded}>{slotActions}</div>
  }

  return (
    <div className={styles.blockEmbedded}>
      <p className={styles.sheetOfferText}>
        <strong>Generamos</strong> especificaciones técnicas para que las revises antes de publicar.
      </p>
      {!canSkill ? (
        <ProjectAutofillInfoNote className={styles.hint}>Elegí el rubro de la lista para editar.</ProjectAutofillInfoNote>
      ) : null}
      {slotActions}
    </div>
  )
}

export default TechnicalSpecCompactEntry
